import { createReducer } from 'typesafe-actions';
import { PDPProductFormatter } from '@westwing/appshell-formatting-helpers';
import { LookDetailState, LookDetailRawData, LookDetail, LookProductResponse } from './types';
import {
    fetchLookDetailPageActions,
    FetchLooksDetailPageSliderActions,
    LookDetailPageActionType,
    LooksDetailPageSliderActionsType,
    setLDPKnownInfoAction,
} from './actions';
import { defaultErrorHandler } from 'Client/redux/dataFetching/generators';
import { PriceFormatConfigOptions } from 'Client/redux/products/types';
import { getPriceFormatOptions } from 'Client/redux/productdetail/helper';
import { defaultLooksSliderState, looksSliderReducer } from 'Client/redux/looksSlider/reducer';
import generateProductPlaceHolders from 'Client/redux/products/productPlaceholder';

const mapLookDetailData = (data: LookDetailRawData, priceFormat?: PriceFormatConfigOptions): LookDetail => {
    const { products, seo: seoData } = data;

    return {
        altTag: data.moodImage?.description,
        id: data.id,
        name: data.name,
        trackingName: data.trackingName || '',
        caption: data.caption,
        slug: data.slug,
        color: data.color,
        isLandscape: data.isLandscape,
        portraitImage: data.portraitListingImage?.file?.url || '',
        products: products.map((value: LookProductResponse, index: number) => ({
            ...PDPProductFormatter(index, value, undefined, undefined, priceFormat),
            ...(value.meta?.hotspot ? { hotspot: value.meta?.hotspot } : {}),
        })),
        seoData: {
            metaDescription: seoData?.metaDescription,
            robotFollow: seoData?.robotFollow,
            index: seoData?.index,
            hreflangs: seoData?.hreflangs,
        },
        moodImage: {
            url: data.moodImage?.file?.url || '',
            seoUrl: data.moodImage?.seo_url || '',
        },
        prevLook: data.prevLook,
        nextLook: data.nextLook,
    };
};

export const defaultLookDetailPageState: LookDetailState = {
    look: {
        altTag: '',
        id: '',
        name: '',
        trackingName: '',
        caption: '',
        slug: '',
        color: '',
        isLandscape: false,
        portraitImage: '',
        products: generateProductPlaceHolders(8),
        moodImage: {
            url: '',
            seoUrl: '',
        },
        seoData: {
            metaDescription: '',
            robotFollow: false,
            index: false,
            hreflangs: {},
        },
        prevLook: null,
        nextLook: null,
    },
    isFetching: false,
    didInvalidate: false,
    identifier: '',
    looksSlider: defaultLooksSliderState,
    error: null,
};

export const lookDetailPageReducer = createReducer<
    LookDetailState,
    LookDetailPageActionType | LooksDetailPageSliderActionsType
>(defaultLookDetailPageState)
    .handleAction(fetchLookDetailPageActions.request, (state, action) => {
        return {
            ...state,
            isFetching: true,
            identifier: action.payload.identifier,
            didInvalidate: false,
            error: undefined,
        };
    })
    .handleAction(fetchLookDetailPageActions.success, (state, action) => {
        const { data: responseData, identifier } = action.payload;

        return {
            ...state,
            look: mapLookDetailData(responseData.data, getPriceFormatOptions(action.payload.reducerPayloadOptions)),
            isFetching: false,
            identifier,
        };
    })
    .handleAction(fetchLookDetailPageActions.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction([...FetchLooksDetailPageSliderActions], (state, action) => {
        return { ...state, looksSlider: looksSliderReducer(true)(state.looksSlider, action) };
    })
    .handleAction(setLDPKnownInfoAction, (state, action) => {
        const {
            payload: {
                look: { name, title, caption, color, isLandscape },
            },
        } = action;
        return {
            ...defaultLookDetailPageState,
            look: {
                ...defaultLookDetailPageState.look,
                name: name || title || '',
                caption: caption || '',
                color: color || '',
                isLandscape: isLandscape || false,
            },
        };
    });
